import { useEffect, useMemo, useState } from 'react'
import { Box, Button, Grid, IconButton, Modal, TextField } from '@material-ui/core'
import CancelIcon from '@material-ui/icons/Cancel'
import { useDispatch } from 'react-redux'
import { Conversation, Role, Stages, TrackingRows } from '@types'
import DocumentsPanel from '../components/DocumentsPanel'
import { recoverRejectedCase } from '../state/trackingSlice2'
import { getRecoveryConversationState } from '../utils/recoveryConversation'
import './modals.css'
import '../track.css'

interface Props {
  isOpen: boolean
  row?: TrackingRows
  role: Role
  isTeam: boolean
  onClose: () => void
}

const formatDate = (date?: string) => {
  if (!date) return ''
  try {
    return new Date(date).toISOString().split('T')[0]
  } catch {
    return ''
  }
}

const MessageBubble = ({ message }: { message: Conversation | null }) => {
  if (!message) return null

  return (
    <Box
      display="flex"
      flexDirection="column"
      mb={1}
      p={1}
      bgcolor="#ffebd9"
      borderRadius="5px"
      boxShadow="0 2px 4px rgba(0, 0, 0, 0.1)"
    >
      <Box fontSize="0.7rem" fontWeight="bold" marginBottom="0.5rem">
        {message.authorName}:
      </Box>
      <Box whiteSpace="pre-line">{message.value}</Box>
      <Box fontSize="0.6rem" color="#fc6404" display="flex" justifyContent="flex-end" width="100%">
        {formatDate(message.date)}
      </Box>
    </Box>
  )
}

const clearRecoveryStages = (stages: Stages = {}): Stages => {
  return {
    ...stages,
    studyDate: '',
    office: '',
    bank: '',
    delivered: '',
    received: '',
    rejected: '',
    documented: '',
    returned: '',
    success: ''
  }
}

const RecoverRejectedModal: React.FC<Props> = ({ isOpen, row, role, isTeam, onClose }) => {
  const dispatch = useDispatch()
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    if (!isOpen) return
    setMessage('')
    setError('')
  }, [isOpen, row?.id])

  const { recoveryReason, recoveryHow } = useMemo(() => getRecoveryConversationState(row?.conversation || []), [
    row?.conversation
  ])

  const onConfirmRecover = () => {
    if (!row?.id) return
    if (!message.trim()) {
      setError('El mensaje es obligatorio.')
      return
    }

    setError('')
    dispatch(
      recoverRejectedCase({
        userId: row.id,
        isTeam,
        stages: clearRecoveryStages(row.stages || {}),
        recoveryMessage: message.trim()
      })
    )
  }

  if (!row) return null

  return (
    <Modal open={isOpen} onClose={onClose} className="documents-modal">
      <form className="modal_main">
        <Grid container>
          <Grid item xs={12} md={6} style={{ padding: '10px' }}>
            <IconButton
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px'
              }}
              onClick={onClose}
            >
              <CancelIcon style={{ color: '#fc6404' }}></CancelIcon>
            </IconButton>
            <DocumentsPanel row={row} role={role} isAddEnable={row.success.length === 0} />
          </Grid>

          <Grid item xs={12} md={6} style={{ padding: '10px' }}>
            <Box fontSize="18px" fontWeight="bold" margin="10px 0 10px 0" color="#fc6404">
              Recuperar caso
            </Box>

            <Box
              display="flex"
              flexDirection="column"
              padding="20px"
              border="1px solid #ccc"
              borderRadius="5px"
              bgcolor="#fff"
              maxHeight="70vh"
              overflow="auto"
            >
              <Box fontWeight="bold" mb={1}>
                Motivo de rechazo
              </Box>
              <MessageBubble message={recoveryReason} />

              <Box fontWeight="bold" mt={2} mb={1}>
                Cómo recuperarlo
              </Box>
              <MessageBubble message={recoveryHow} />

              <TextField
                size="small"
                fullWidth
                multiline
                label="Mensaje de recuperación"
                variant="outlined"
                margin="normal"
                required
                value={message}
                onChange={e => setMessage(e.target.value)}
              />
              {error ? (
                <Box color="#d32f2f" fontSize="0.8rem" mb={1}>
                  {error}
                </Box>
              ) : null}
            </Box>

            <Box display="flex" justifyContent="flex-end" marginTop="12px" gridGap="8px">
              <Button type="button" variant="contained" color="default" onClick={onClose}>
                Cancelar
              </Button>
              <Button type="button" variant="contained" color="primary" onClick={onConfirmRecover}>
                Marcar como recuperado
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Modal>
  )
}

export default RecoverRejectedModal
